/* Created by Interactive 63 - https://interactive63.com */
/**
 * Conversion of easings to CSS timing functions and to short labels for the interface.
 */
import { formatNumber } from './format-number';
import { pointsForEasing, pointsToLinearCss } from './linear-fn';
import type { Easing } from './types';

export interface CssEasing {
  value: string;
  /** Declared before `value` so browsers without `linear()` fall back to it. */
  fallback?: string;
}

const OVERSHOOT_FALLBACK = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
const OVERSHOOT_EPSILON = 0.001;

const STEP_LABELS = {
  'jump-start': 'start',
  'jump-end': 'end',
  'jump-none': 'none',
  'jump-both': 'both',
} as const;

/** Limits a number to 0..1. */
function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Returns the CSS timing function for an easing, with a fallback for `linear()` curves. */
export function easingToCss(easing: Easing): CssEasing {
  switch (easing.type) {
    case 'linear':
      return { value: 'linear' };
    case 'keyword':
      return { value: easing.name };
    case 'cubic': {
      const [x1, y1, x2, y2] = [clamp01(easing.x1), easing.y1, clamp01(easing.x2), easing.y2].map(
        (n) => formatNumber(n, 3),
      );
      return { value: `cubic-bezier(${x1}, ${y1}, ${x2}, ${y2})` };
    }
    case 'steps':
      return { value: `steps(${easing.count}, ${easing.position})` };
    case 'hold':
      return { value: 'steps(1, jump-end)' };
    case 'spring': {
      const points = pointsForEasing(easing);
      const overshoots = points.some(([, y]) => y > 1 + OVERSHOOT_EPSILON);
      return {
        value: pointsToLinearCss(points),
        fallback: overshoots ? OVERSHOOT_FALLBACK : 'ease-out',
      };
    }
    case 'bounce':
      return { value: pointsToLinearCss(pointsForEasing(easing)), fallback: 'ease-out' };
    case 'elastic':
      return { value: pointsToLinearCss(pointsForEasing(easing)), fallback: OVERSHOOT_FALLBACK };
  }
}

/** Returns a short human-readable name for an easing, such as "Ease in-out". */
export function easingLabel(easing: Easing): string {
  switch (easing.type) {
    case 'linear':
      return 'Linear';
    case 'keyword':
      return easing.name === 'ease' ? 'Ease' : `Ease ${easing.name.slice('ease-'.length)}`;
    case 'cubic':
      return `Cubic Bézier (${[easing.x1, easing.y1, easing.x2, easing.y2]
        .map((n) => formatNumber(n, 3))
        .join(', ')})`;
    case 'steps':
      return `Steps (${easing.count}, ${STEP_LABELS[easing.position]})`;
    case 'hold':
      return 'Hold';
    case 'spring':
      return `Spring (${easing.stiffness} / ${easing.damping} / ${easing.mass})`;
    case 'bounce':
      return `Bounce (${easing.bounces} / ${easing.decay})`;
    case 'elastic':
      return `Elastic (${easing.oscillations} / ${easing.decay})`;
  }
}
